import { useEffect, useMemo, useState } from 'react';
import {
  addDoc, collection, deleteDoc, doc, onSnapshot, query, serverTimestamp, where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../contexts/LocaleContext';

// ── Tour notes ───────────────────────────────────────────────────────────────
// Notes written by staff, each stamped with who wrote it and when. A note is
// saved to the tour, to the tour's primary contact, or both:
//
//   requestId    — set for "tour" and "both": shows on this tour
//   contactEmail — set for "customer" and "both": shows on EVERY tour booked
//                  by that contact, past and future
//
// The contact is keyed on the lower-cased email, the same key quote merging
// uses, since customerId isn't set on every tour. customerEmail comes first
// because it's the field staff edit when the primary contact changes.
//
// Tour leaders get a readOnly view of the notes saved to the tour ("This tour"
// and "Both"); customer-only notes stay with staff (see firestore.rules).

export const SCOPES = [
  { key: 'tour', label: 'This tour' },
  { key: 'customer', label: 'Customer' },
  { key: 'both', label: 'Both' },
];

export function contactKeyOf(request) {
  return String(request?.customerEmail || request?.customerEmailLower || '').trim().toLowerCase();
}

/** Build the Firestore doc for a new note. */
export function buildNote({ text, scope, request, user }) {
  const contactEmail = contactKeyOf(request);
  const toTour = scope === 'tour' || scope === 'both';
  const toCustomer = (scope === 'customer' || scope === 'both') && !!contactEmail;
  return {
    text: text.trim(),
    requestId: toTour || !toCustomer ? request.id : null,
    contactEmail: toCustomer ? contactEmail : null,
    contactName: toCustomer ? (request.contactName || request.groupName || '') : null,
    originRequestId: request.id,
    originTourLabel: request.internalRef || request.tourName || request.groupName || '',
    authorUid: user.uid,
    authorName: user.displayName || user.email || '',
    authorEmail: user.email || '',
    createdAt: serverTimestamp(),
  };
}

/** Merge the tour's and the contact's notes, newest first, without duplicates. */
export function mergeNotes(tourNotes, contactNotes) {
  const byId = new Map();
  for (const n of [...tourNotes, ...contactNotes]) byId.set(n.id, n);
  const ms = (n) => (n.createdAt?.toMillis ? n.createdAt.toMillis() : Number.MAX_SAFE_INTEGER); // pending write = newest
  return [...byId.values()].sort((a, b) => ms(b) - ms(a));
}

function fmt(ts, t) {
  if (!ts?.toDate) return t('notes.justNow');
  return ts.toDate().toLocaleString(t.dateLocale, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function TourNotes({ request, legacyNotes = '', readOnly = false }) {
  const { currentUser, isAdmin } = useAuth();
  const t = useT();
  const requestId = request.id;
  const contactEmail = readOnly ? '' : contactKeyOf(request);

  const [tourNotes, setTourNotes] = useState([]);
  const [contactNotes, setContactNotes] = useState([]);
  const [text, setText] = useState('');
  const [scope, setScope] = useState('tour');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'tourNotes'), where('requestId', '==', requestId)),
      (snap) => setTourNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('Tour notes subscription failed', err)
    );
    return unsub;
  }, [requestId]);

  useEffect(() => {
    if (!contactEmail) { setContactNotes([]); return undefined; }
    const unsub = onSnapshot(
      query(collection(db, 'tourNotes'), where('contactEmail', '==', contactEmail)),
      (snap) => setContactNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('Customer notes subscription failed', err)
    );
    return unsub;
  }, [contactEmail]);

  const notes = useMemo(() => mergeNotes(tourNotes, contactNotes), [tourNotes, contactNotes]);

  async function add() {
    if (!text.trim() || !currentUser) return;
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'tourNotes'), buildNote({ text, scope, request, user: currentUser }));
      setText('');
    } catch (err) {
      console.error('Failed to add note', err);
      setError('Could not save the note. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(noteId) {
    try {
      await deleteDoc(doc(db, 'tourNotes', noteId));
    } catch (err) {
      console.error('Failed to delete note', err);
      setError('Could not delete the note.');
    }
    setConfirmDelete(null);
  }

  return (
    <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[14px] font-bold text-[#111827]">{t('notes.title')}</h3>
        {!readOnly && <span className="text-[11.5px] text-[#9CA3AF]">Staff + tour leaders</span>}
      </div>

      {!readOnly && (<>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={5000}
        placeholder="Add a note…"
        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-y"
      />

      <div className="mt-2">
        <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-[0.6px] mb-1">Save to</p>
        <div className="grid grid-cols-3 rounded-[8px] border border-[#E5E7EB] overflow-hidden" role="radiogroup" aria-label="Save note to">
          {SCOPES.map((s) => {
            const disabled = s.key !== 'tour' && !contactEmail;
            const active = scope === s.key;
            return (
              <button
                key={s.key}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={disabled}
                onClick={() => setScope(s.key)}
                className={`py-[6px] text-[12px] font-semibold transition border-l first:border-l-0 border-[#E5E7EB] disabled:opacity-40 disabled:cursor-not-allowed ${
                  active ? 'bg-pb text-white' : 'bg-white text-[#374151] hover:bg-surface'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <p className="text-[11.5px] text-[#9CA3AF] mt-1.5 leading-snug">
          {!contactEmail
            ? 'Add a primary contact email to save notes against the customer.'
            : scope === 'tour'
              ? "Shows on this tour only. This tour's tour leaders can see it."
              : scope === 'both'
                ? `Shows on every tour booked by ${request.contactName || contactEmail}, including this one. This tour's tour leaders can see it.`
                : `Shows on every tour booked by ${request.contactName || contactEmail}. Staff only.`}
        </p>
      </div>

      <div className="flex justify-end mt-2">
        <button
          onClick={add}
          disabled={saving || !text.trim()}
          className="px-[14px] py-[7px] text-[12.5px] font-bold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Add note'}
        </button>
      </div>
      {error && <p className="text-[12px] text-dr mt-2">{error}</p>}
      </>)}

      <div className={readOnly ? 'border-t border-[#F1F3F6]' : 'mt-4 border-t border-[#F1F3F6]'}>
        {notes.length === 0 && !legacyNotes && (
          <p className="text-[12.5px] text-[#9CA3AF] text-center pt-4">{t('notes.none')}</p>
        )}
        {notes.map((n) => {
          const onCustomer = !!n.contactEmail;
          const fromOtherTour = n.originRequestId && n.originRequestId !== requestId;
          const canDelete = !readOnly && (isAdmin || n.authorUid === currentUser?.uid);
          return (
            <div key={n.id} className="py-3 border-b border-[#F1F3F6] last:border-b-0 group">
              <div className="flex flex-wrap gap-1 mb-1.5">
                {n.requestId === requestId && (
                  <span className="text-[10.5px] font-bold px-1.5 py-[1px] rounded-[5px] bg-[#F3F4F6] text-[#4B5563]">{t('notes.tour')}</span>
                )}
                {onCustomer && !readOnly && (
                  <span className="text-[10.5px] font-bold px-1.5 py-[1px] rounded-[5px] bg-blue-light text-pb">Customer</span>
                )}
                {fromOtherTour && (
                  <span className="text-[10.5px] font-semibold px-1.5 py-[1px] rounded-[5px] bg-[#FEF3C7] text-[#92400E] truncate max-w-full">
                    {t('notes.fromTour', { label: n.originTourLabel || t('notes.anotherTour') })}
                  </span>
                )}
              </div>
              <p className="text-[13px] text-[#111827] whitespace-pre-wrap break-words">{n.text}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <p className="text-[11.5px] text-[#9CA3AF]">
                  {n.authorName || n.authorEmail || t('notes.unknown')} · {fmt(n.createdAt, t)}
                </p>
                {canDelete && (
                  confirmDelete === n.id ? (
                    <span className="ml-auto flex gap-2">
                      <button onClick={() => remove(n.id)} className="text-[11.5px] font-bold text-dr">Delete</button>
                      <button onClick={() => setConfirmDelete(null)} className="text-[11.5px] font-semibold text-[#9CA3AF]">Keep</button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(n.id)}
                      className="ml-auto text-[11.5px] font-semibold text-[#9CA3AF] hover:text-dr opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                    >
                      Delete
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}

        {/* The old single free-text box, kept read-only so nothing is lost. */}
        {legacyNotes && legacyNotes.trim() && (
          <div className="py-3">
            <span className="text-[10.5px] font-bold px-1.5 py-[1px] rounded-[5px] bg-[#F3F4F6] text-[#6B7280]">{t('notes.earlier')}</span>
            <p className="text-[13px] text-[#374151] whitespace-pre-wrap break-words mt-1.5">{legacyNotes}</p>
            <p className="text-[11.5px] text-[#9CA3AF] mt-1.5">{t('notes.legacyHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
